import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Pluralize from 'react-pluralize';
import { Confirm } from 'react-confirm-bootstrap';
import { Link, withRouter } from 'react-router-dom';
import { Typeahead } from 'react-bootstrap-typeahead';
import { SplitButton, MenuItem } from 'react-bootstrap/lib';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import BulkAccessoryEditModal from '../BulkAccessoryEditModal';

function AccessoriesList({ accessoryConfig, dealer, history, updateConfig }) {
  const [bulkEdit, setBulkEdit] = useState(false);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({});

  const accessories = (accessoryConfig || {}).accessories || [];
  const categories = (accessoryConfig || {}).categories || [];

  useEffect(() => {
    setSelected([]);
  }, [bulkEdit]);

  const editColumn = (_, accessory) => (
    <SplitButton
      title="Edit"
      id={`split-button-basic-${accessory.id}`}
      onClick={() =>
        history.push(`/${dealer.id}/accessories/accessory/${accessory.id}`)
      }
    >
      <Confirm
        confirmText="Confirm Delete"
        title={`Deleting ${accessory.name}`}
        body={`Are you sure you want to delete ${accessory.name}?`}
        onConfirm={() => {
          const newAccessories = accessories.filter(
            (a) => a.id !== accessory.id,
          );
          updateConfig({ accessories: newAccessories });
        }}
      >
        <MenuItem>Delete</MenuItem>
      </Confirm>
    </SplitButton>
  );

  const allColumn = (_, a) => {
    const isSelected = selected.find((s) => s.id === a.id) !== undefined;
    return (
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {
          if (isSelected) {
            setSelected((prev) => prev.filter((s) => s.id !== a.id));
          } else {
            setSelected((prev) => prev.concat(a));
          }
        }}
      />
    );
  };

  const filteredAccessories = accessories.filter((a) => {
    const name = a.name.toLowerCase();
    const partNumber = a.partNumber.toLowerCase();
    const categoryIds = a.categoryIds || [];
    if (
      filters.partNumber &&
      !partNumber.includes(filters.partNumber.toLowerCase())
    ) {
      return false;
    }
    if (filters.name && !name.includes(filters.name.toLowerCase())) {
      return false;
    }
    if (
      (filters.categoryIds || []).length > 0 &&
      !filters.categoryIds.every((id) => categoryIds.includes(id))
    ) {
      return false;
    }
    return true;
  });

  return (
    <div>
      <h3>
        <Pluralize
          singular="Accessory"
          plural="Accessories"
          count={filteredAccessories.length}
        />
      </h3>
      <div className="text-right">
        {bulkEdit && (
          <button
            className="btn btn-primary"
            disabled={selected.length === 0}
            onClick={() =>
              BulkAccessoryEditModal({
                accessoryConfig,
                selected,
                updateConfig,
                setBulkEdit,
              })
            }
          >
            Edit Selected
          </button>
        )}{' '}
        <button
          className="btn btn-primary"
          onClick={() => setBulkEdit(!bulkEdit)}
          disabled={accessories.length === 0}
        >
          {bulkEdit ? 'Exit Bulk Edit' : 'Bulk Edit'}
        </button>{' '}
        {!bulkEdit && (
          <Link
            className="btn btn-primary"
            to={`/${dealer.id}/accessories/accessory`}
          >
            New Accessory
          </Link>
        )}
      </div>
      <div className="row accessory-filter-row">
        <div className="form-group col-sm-3">
          <input
            className="form-control"
            placeholder="Part Number"
            value={filters.partNumber}
            disabled={bulkEdit}
            onChange={(e) =>
              setFilters({ ...filters, partNumber: e.target.value })
            }
          />
        </div>
        <div className="form-group col-sm-3">
          <input
            className="form-control"
            placeholder="Name"
            value={filters.name}
            disabled={bulkEdit}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
        </div>
        <div className="form-group col-sm-3">
          <Typeahead
            id="accessory-typeahead"
            className="picker"
            selectHintOnEnter
            multiple
            clearButton
            labelKey="name"
            placeholder="Category"
            options={categories}
            disabled={bulkEdit}
            selected={categories.filter((c) =>
              (filters.categoryIds || []).includes(c.id),
            )}
            onChange={(values) =>
              setFilters({ ...filters, categoryIds: values.map((c) => c.id) })
            }
          />
        </div>
      </div>
      <BootstrapTable
        striped
        hover
        data={filteredAccessories}
        tableStyle={{ border: 'none', padding: '20px 0' }}
        bodyStyle={{ paddingBottom: '35px' }}
      >
        {bulkEdit && (
          <TableHeaderColumn width="60px" dataFormat={allColumn}>
            <input
              type="checkbox"
              checked={selected.length === filteredAccessories.length}
              onChange={() => {
                const isAllSelected =
                  selected.length === filteredAccessories.length;
                setSelected(isAllSelected ? [] : [...filteredAccessories]);
              }}
            />{' '}
            All
          </TableHeaderColumn>
        )}
        <TableHeaderColumn isKey dataField="partNumber">
          Part Number
        </TableHeaderColumn>
        <TableHeaderColumn dataField="name">Name</TableHeaderColumn>
        <TableHeaderColumn
          dataField="categoryIds"
          dataFormat={(_, accessory) => {
            const categoryIds = accessory.categoryIds || [];
            return categories
              .filter((c) => categoryIds.includes(c.id))
              .map((c) => c.name)
              .join(', ');
          }}
        >
          Category
        </TableHeaderColumn>
        <TableHeaderColumn dataField="type">Type</TableHeaderColumn>
        <TableHeaderColumn
          columnClassName="list-action-column"
          dataFormat={editColumn}
        />
      </BootstrapTable>
    </div>
  );
}

AccessoriesList.propTypes = {
  accessoryConfig: PropTypes.object,
  dealer: PropTypes.object,
  history: PropTypes.object,
  updateConfig: PropTypes.func,
};

export default withRouter(AccessoriesList);
